import { Box, Flex, Text } from '@chakra-ui/react';
import StepOne from '../../components/UserRegistrationSteps/StepOne';
import StepTwo from '../../components/UserRegistrationSteps/StepTwo/StepTwo';
import StepThree, {
  CustomErrorMessage
} from '../../components/UserRegistrationSteps/StepThree/StepThree';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  getUserDetails,
  getVerifyExpireLink,
  useActivateAccountMutation,
  useUserDetailsMutation
} from '../../api/signUpMutation';
import { IUserDraft } from '@publishing-engine/domain/models';
import { BrandLogo } from '../SignUpInviteSent';
import { Button, useToast } from '@morressier/alembic';
import { AxiosError } from 'axios';
import SignUpMenu from './SignUpMenu';
import { FormProvider, useForm } from 'react-hook-form';
import {
  buildRowDuplicateMessage,
  findRowLevelDuplicates,
  handleDuplicateCheck,
  IUserDetailsStepThreeForm,
  pickFields,
  StepOneForm,
  StepTwoForm,
  stepTwoFormFields
} from 'src/components/UserRegistrationSteps/StepThree/signupHook';
import SubmitButton from 'src/components/UserRegistrationSteps/SubmitButton';
import { useQuery } from '@tanstack/react-query';
import { toastMessage } from '../SignIn/ResetPassword';

const normalizeAdditionalEmails = (
  emails?: Array<{ email?: string } | null | undefined>
) =>
  Array.from(
    new Set(
      (emails ?? [])
        .map(entry => entry?.email?.trim().toLowerCase())
        .filter((email): email is string => Boolean(email))
    )
  );

export type IStepProps = {
  currentStep: number;
  totalSteps: number;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  user?: IUserDraft | null | undefined;
  setCustomErrorMessage: (value: CustomErrorMessage) => void;
  customErrorMessage: CustomErrorMessage;
  isLoading: boolean;
};

const steps = [StepOne, StepTwo, StepThree];

const UserDetailsForm = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();

  const draftStepParam = searchParams.get('draftStep');
  const draftStep = draftStepParam === null ? NaN : Number(draftStepParam);
  const initialDraftStepParamRef = useRef(draftStepParam);
  const tokenRef = useRef(searchParams.get('token'));
  const token = tokenRef.current;
  const initialDraftStep = Number(initialDraftStepParamRef.current);
  const isResumeFlow =
    initialDraftStepParamRef.current !== null &&
    !Number.isNaN(initialDraftStep);

  const formContainerRef = useRef<HTMLDivElement>(null);

  const [currentStep, setCurrentStep] = useState(1);
  const [isResumeStarted, setIsResumeStarted] = useState(!isResumeFlow);
  const [registeredUser, setRegisteredUserData] = useState<IUserDraft | null>(
    null
  );

  const [customErrorMessage, setCustomErrorMessage] =
    useState<CustomErrorMessage>({
      dateOfBirth: '',
      orcid: false
    });

  const stepOneMethods = useForm<StepOneForm>({
    mode: 'onChange',
    reValidateMode: 'onBlur'
  });

  const stepTwoMethods = useForm<StepTwoForm>({
    defaultValues: {
      affiliationDetails: {},
      additionalAffiliations: [{}]
    }
  });

  const stepThreeMethods = useForm<IUserDetailsStepThreeForm>({
    defaultValues: {} as IUserDetailsStepThreeForm,
    mode: 'onChange'
  });

  const verifyExpireLinkQuery = useQuery({
    queryKey: ['verify-expire-link', token],
    queryFn: () => getVerifyExpireLink(token!),
    // Fresh signup already validates the link (then verifies the email) in
    // SignUpForm, so only re-validate here for the resume flow, which reaches
    // this page directly and skips email verification.
    enabled: !!token && isResumeFlow && isResumeStarted,
    retry: false,
    onSuccess: data => {
      if (data?.isExpired) {
        toast({ variant: 'error', ...toastMessage });
        navigate('/signup');
      }
    },
    onError: (error: AxiosError<{ isExpired?: boolean }>) => {
      const isExpired = error?.response?.data?.isExpired;

      if (isExpired) {
        toast({ variant: 'error', ...toastMessage });
        navigate('/signup');
      } else {
        toast({ variant: 'error', ...toastMessage });
        navigate('/signup');
      }
    }
  });

  const isValidLink =
    !isResumeFlow ||
    (verifyExpireLinkQuery.isSuccess &&
      verifyExpireLinkQuery.data?.isExpired === false);

  const signUpDetailsMutation = useUserDetailsMutation();
  const getRegistrationActivationMutation = useActivateAccountMutation();

  const isFormSubmitting =
    signUpDetailsMutation.isLoading ||
    getRegistrationActivationMutation.isLoading ||
    false;

  const handleSubmitByStep = async () => {
    if (currentStep === 1) {
      const {
        trigger,
        getValues,
        formState: { errors },
        setFocus
      } = stepOneMethods;
      const isValid = await trigger();

      if (!isValid) {
        const firstErrorField = Object.keys(errors)[0];
        if (firstErrorField) setFocus(firstErrorField as any);
        return;
      }

      if (!isValid || customErrorMessage.orcid) {
        trigger('orcid');
        return;
      }

      const values = getValues();
      const phoneNumberValue = values.profileDetails?.phoneNumber?.value || '';
      const countryCallingCode = phoneNumberValue
        ? values.profileDetails?.phoneNumber?.countryCallingCode ?? ''
        : '';
      const countryCode = phoneNumberValue
        ? values.profileDetails?.phoneNumber?.country ?? ''
        : '';

      const payload = {
        prefix: values.prefix,
        firstName: values.firstName?.trim() || '',
        middleName: values.middleName?.trim() || '',
        lastName: values.lastName?.trim() || '',
        orcid: values.orcid?.trim() || '',
        email: registeredUser?.email?.trim() || '',
        secondaryEmail: values.secondaryEmail?.trim() || '',
        ccEmail: values.ccEmail?.trim() || '',
        additionalEmails: normalizeAdditionalEmails(values.additionalEmails),
        degree: values.degree?.trim() || '',
        jobTitle: values.jobTitle?.trim() || '',
        linkedInUrl: (values.linkedInUrl || '').trim(),
        password: values.password,
        profileDetails: {
          phoneNumber: phoneNumberValue,
          countryCallingCode,
          countryCode,
          linkedinProfileUrl: values?.profileDetails?.linkedinProfileUrl || ''
        }
      };

      const response = await signUpDetailsMutation.mutateAsync({
        userId: userId ?? '',
        payload: { draftStep: currentStep, ...payload }
      });

      if (!response.isSuccess) {
        // Extract message from details array if available, otherwise use generic message
        const details = response?.message?.details;
        const message =
          typeof response?.message === 'string'
            ? response.message
            : Array.isArray(details) &&
              details.length > 0 &&
              details[0]?.message
            ? details[0].message
            : response?.message?.message || 'Internal Server Error';

        toast({
          variant: 'error',
          title: 'Error',
          description: message,
          isClosable: true
        });
        return;
      }
      goToNextStep();
    } else if (currentStep === 2) {
      const {
        trigger,
        getValues,
        setFocus,
        formState: { errors }
      } = stepTwoMethods;

      const valid = await trigger();

      if (!valid) {
        if (Object.keys(errors).length > 0) {
          const firstErrorField = Object.keys(errors)[0] as keyof StepTwoForm;
          setFocus(firstErrorField as any);
        }
        return;
      }

      const { affiliationDetails, additionalAffiliations } = getValues();

      const filteredAffiliationDetails = pickFields(
        affiliationDetails || {},
        stepTwoFormFields
      );
      const filteredAdditionalAffiliations =
        additionalAffiliations?.map(a => pickFields(a, stepTwoFormFields)) ||
        [];

      const lastIdx = filteredAdditionalAffiliations.length - 1;
      const selectedAffiliation = filteredAdditionalAffiliations[lastIdx];

      if (selectedAffiliation && filteredAdditionalAffiliations.length > 0) {
        const isDuplicateMatches = handleDuplicateCheck(
          selectedAffiliation,
          `additionalAffiliations.${lastIdx}`,
          filteredAdditionalAffiliations,
          filteredAffiliationDetails
        );

        if (isDuplicateMatches.length) {
          const duplicatesLabel = isDuplicateMatches
            .map(m =>
              m.index === 'primary'
                ? 'Primary affiliation'
                : `Secondary affiliation(s) ${Number(m.index) + 1}`
            )
            .join(', ');

          toast({
            variant: 'error',
            title: 'Duplicate affiliation detected',
            description: `Secondary affiliation(s) ${
              lastIdx + 1
            } is identical to ${duplicatesLabel}. Please modify one of these entries to continue.`,
            isClosable: true
          });

          return;
        }
      }
      const payload = {
        affiliationDetails: filteredAffiliationDetails,
        additionalAffiliations: filteredAdditionalAffiliations
      };

      const response = await signUpDetailsMutation.mutateAsync({
        userId: registeredUser?.id ?? '',
        payload: { draftStep: currentStep, ...payload }
      });

      if (!response.isSuccess) {
        const message = response?.message?.details?.length
          ? response.message.details[0]?.message
          : response?.message?.message || 'Internal Server Error';

        toast({
          variant: 'error',
          title: 'Error',
          description: message,
          isClosable: true
        });
        return;
      }

      toast({
        variant: 'success',
        title: 'Affiliation Saved',
        description: response?.message ?? 'Success',
        isClosable: true
      });

      goToNextStep();
    } else if (currentStep === 3) {
      const {
        trigger,
        getValues,
        formState: { errors },
        setFocus,
        watch
      } = stepThreeMethods;

      const { researchFocusAndExpertise } = getValues();
      const liveData =
        getValues('researchFocusAndExpertise.specialtyAndKeywords') ||
        watch('researchFocusAndExpertise.specialtyAndKeywords') ||
        [];
      const rowDuplicateResult = findRowLevelDuplicates(liveData);

      if (rowDuplicateResult.hasDuplicates) {
        toast({
          variant: 'error',
          title: 'Duplicate entry detected',
          description: (
            <Flex direction={'column'}>
              {rowDuplicateResult.duplicateRows.map(issue => (
                <Text
                  key={`${issue.rowIndex}-${issue.value}-${issue.fieldKeys.join(
                    '-'
                  )}`}
                >
                  {buildRowDuplicateMessage(issue)}
                </Text>
              ))}
            </Flex>
          ),
          isClosable: true
        });

        return;
      }

      const valid = await trigger();

      if (!valid) {
        const firstErrorField = Object.keys(errors)[0];
        if (firstErrorField) setFocus(firstErrorField as any);
        return;
      }

      const reqBody = {
        draftStep: currentStep,
        researchFocusAndExpertise: {
          reviewerInterest: researchFocusAndExpertise.reviewerInterest,
          scandinavianKeywords: researchFocusAndExpertise?.scandinavianKeywords,
          professionalInterests:
            researchFocusAndExpertise?.professionalInterests,
          unavailabilityPeriodFrom:
            researchFocusAndExpertise?.reviewerInterest === 'Yes'
              ? researchFocusAndExpertise?.unavailabilityPeriodFrom
              : '',
          unavailabilityPeriodTo:
            researchFocusAndExpertise?.reviewerInterest === 'Yes'
              ? researchFocusAndExpertise?.unavailabilityPeriodTo
              : '',

          specialtyAndKeywords: researchFocusAndExpertise?.specialtyAndKeywords
            ? researchFocusAndExpertise?.specialtyAndKeywords.map(item => ({
                specialtyName: item?.specialty?.name,
                specialtyId: item?.specialty?.termId,
                subSpecialtyName: item?.subSpecialty?.name,
                subSpecialtyId: item?.subSpecialty?.termId,
                keyword: item?.keyword?.name,
                keywordId: item?.keyword?.termId,
                scandinavianKeyword: item?.scandinavianKeyword?.name,
                scandinavianKeywordId: item?.scandinavianKeyword?.termId
              }))
            : []
        }
      };

      try {
        const response = await signUpDetailsMutation.mutateAsync({
          userId: registeredUser?.id ?? '',
          payload: reqBody
        });

        if (!response.isSuccess) {
          const message = response?.message?.details?.length
            ? response.message.details[0]?.message
            : response?.message?.message || 'Internal Server Error';

          toast({
            variant: 'error',
            title: 'Error',
            description: message,
            isClosable: true
          });

          return;
        }

        if (response?.data?.isFormSubmitted) {
          const activationResponse =
            await getRegistrationActivationMutation.mutateAsync({
              userId: registeredUser?.id ?? ''
            });

          if (activationResponse?.isSuccess) {
            toast({
              variant: 'success',
              title: 'Account Activated',
              description: activationResponse?.message,
              isClosable: true
            });

            navigate(`/signup/account-activated/${registeredUser?.id}`);
          } else {
            toast({
              variant: 'error',
              title: 'Activation Failed',
              description:
                activationResponse?.message?.message || 'Internal Server Error',
              isClosable: true
            });
          }
        }
      } catch (err: any) {
        toast({
          variant: 'error',
          title: 'Unexpected Error',
          description: err?.message || 'Something went wrong.',
          isClosable: true
        });
      }
    }
  };

  const updateDraftStepInUrl = (step: number) => {
    const url = new URL(window.location.href);

    url.searchParams.set('draftStep', step.toString());

    if (token) {
      url.searchParams.set('token', token);
    }

    window.history.replaceState(null, '', url.toString());
  };

  useEffect(() => {
    const fetchUserDraftStep = async () => {
      if (!userId) return;

      try {
        const response = await getUserDetails(userId);

        if (response?.data?.draftStep !== undefined) {
          const step = response.data.draftStep;
          updateDraftStepInUrl(step);
          // const url = new URL(window.location.href);

          // url.searchParams.set(
          //   'draftStep',
          //   currentStep !== step ? draftStep.toString() : step.toString()
          // );

          // window.history.replaceState(null, '', url.toString());
          setRegisteredUserData(response.data);
        }
      } catch (error) {
        const errorObj = error as AxiosError;

        toast({
          variant: 'error',
          title: 'Error',
          description: errorObj?.response?.statusText || 'Internal Server Error'
        });

        navigate('/');
      }
    };

    if (isValidLink && (!isResumeFlow || isResumeStarted)) {
      fetchUserDraftStep();
    }
  }, [
    isValidLink,
    isResumeFlow,
    isResumeStarted,
    currentStep,
    userId,
    draftStep
  ]);

  const goToNextStep = () => {
    setCurrentStep(prev => {
      const nextStep = prev + 1;
      updateDraftStepInUrl(nextStep);
      return nextStep;
    });
  };

  const goToPreviousStep = () => {
    setCurrentStep(prev => {
      const previousStep = Math.max(prev - 1, 1);
      updateDraftStepInUrl(previousStep);
      return previousStep;
    });
  };

  const handleResumeSignup = () => {
    setIsResumeStarted(true);
  };

  const handleStepRender = () => {
    const Component = steps[currentStep - 1];
    if (!Component) return null;

    let currentMethods: any;

    if (currentStep === 1) currentMethods = stepOneMethods;
    else if (currentStep === 2) currentMethods = stepTwoMethods;
    else if (currentStep === 3) currentMethods = stepThreeMethods;

    const { handleSubmit } = currentMethods;

    const commonProps = {
      currentStep,
      totalSteps: steps.length,
      goToNextStep,
      goToPreviousStep
    };

    const additionalProps = {
      user: registeredUser,
      customErrorMessage,
      setCustomErrorMessage,
      isLoading: isFormSubmitting,
      formContainerRef
    };

    return (
      <Flex direction="column" minH="100vh" w="full">
        <Flex
          py={2}
          px={4}
          justify="space-between"
          align="center"
          boxShadow="elevation3"
          position="sticky"
          top={0}
          zIndex={1000}
          bgColor="surfacePrimaryFill"
          flexShrink={0}
        >
          <Flex align="center" gap={2}>
            <BrandLogo />
          </Flex>
        </Flex>

        <Box
          display={{ base: 'block', md: 'none' }}
          position="sticky"
          top="60px"
          zIndex={999}
          bgColor="surfacePrimaryFill"
          boxShadow="elevation2"
        >
          <SignUpMenu
            setCurrentStep={setCurrentStep}
            draftStep={currentStep}
            user={registeredUser}
            scrollContainerRef={formContainerRef}
          />
        </Box>

        <Flex
          flex={1}
          overflow="hidden"
          p={{ base: 2, md: 3 }}
          gap={3}
          pt={{ base: 3, md: 5 }}
        >
          <Box
            display={{ base: 'none', md: 'block' }}
            bgColor="surfacePrimaryFill"
            p={3}
            borderRadius="10px"
            flexShrink={0}
            overflowY="hidden"
            position="sticky"
            top="90px"
            height="calc(100vh - 90px)"
            boxShadow="elevation2"
          >
            <SignUpMenu
              setCurrentStep={setCurrentStep}
              draftStep={currentStep}
              user={registeredUser}
              scrollContainerRef={formContainerRef}
            />
          </Box>

          <Box flex={1} ref={formContainerRef} overflowY="auto">
            <FormProvider {...currentMethods}>
              <form
                autoComplete="on"
                style={{
                  flex: 1,
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}
                onSubmit={handleSubmit(handleSubmitByStep)}
              >
                <Box pb={20}>
                  <Component {...commonProps} {...additionalProps} />
                </Box>

                <Box position="absolute" bottom={0} left={0} right={0}>
                  <Flex
                    position="sticky"
                    bottom={0}
                    left={0}
                    right={0}
                    px={{ base: 3, lg: 4 }}
                    bgColor="surfacePrimaryFill"
                    zIndex={1004}
                    justify="flex-end"
                    gap={3}
                    flexShrink={0}
                    boxShadow="elevation2"
                  >
                    <SubmitButton margin={0} isLoading={isFormSubmitting} />
                  </Flex>
                </Box>
              </form>
            </FormProvider>
          </Box>
        </Flex>
      </Flex>
    );
  };

  if (isResumeFlow && !isResumeStarted) {
    return (
      <Flex
        height="100%"
        flexGrow={2}
        flexBasis={0}
        minHeight={'100%'}
        direction="column"
      >
        <Flex
          flexDirection="column"
          gap={12}
          width={{ base: '95%', md: '80%' }}
          marginX={'auto'}
          flex={1}
        >
          <Flex justifyContent={'space-between'} alignItems={'end'}>
            <BrandLogo />
          </Flex>

          <Flex flex="1" alignItems="center" justifyContent="center" px={4}>
            <Box textAlign="center" maxW="640px" width="full">
              <Text textStyle="headingGroup">Resume Signup</Text>
              <Text textAlign="center" maxW="640px">
                Continue where you left off by resuming your saved signup draft.
              </Text>
              <Button onClick={handleResumeSignup} my={8}>
                Resume Signup
              </Button>
            </Box>
          </Flex>
        </Flex>
      </Flex>
    );
  }

  if (!isValidLink) {
    return null;
  }

  return handleStepRender();
};

export default UserDetailsForm;
